/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { AsyncPipe } from "@angular/common";
import { Component, Inject } from "@angular/core";
import { FormBuilder, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subject, last, scan } from "rxjs";
import { FormActionsComponent, FormComponent, FormInputComponent, ModalComponent, rangeValidator } from "typedb-cloud-framework";
import { Cluster, ClusterLogLine } from "../../../../concept/cluster";
import { ClusterApi } from "../../../../service/cluster/cluster-api.service";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { DialogResult } from "../../../../service/dialog.service";
import { SnackbarService } from "../../../../service/snackbar.service";

export type DownloadClusterLogsData = { cluster: Cluster };

@Component({
    selector: "tp-download-cluster-logs-dialog",
    templateUrl: "./download-cluster-logs-dialog.component.html",
    styleUrls: ["./download-cluster-logs-dialog.component.scss"],
    imports: [
        ModalComponent, AsyncPipe, FormInputComponent, FormActionsComponent, FormsModule, ReactiveFormsModule,
        FormComponent, MatFormFieldModule, MatInputModule
    ]
})
export class DownloadClusterLogsDialogComponent {
    readonly isSubmitting$ = new Subject<boolean>();
    readonly form = this.formBuilder.nonNullable.group({
        limit: [10_000, [rangeValidator(1, 10_000_000)]],
    });

    constructor(
        private dialogRef: MatDialogRef<DownloadClusterLogsDialogComponent, DialogResult>,
        @Inject(MAT_DIALOG_DATA) private data: DownloadClusterLogsData, private formBuilder: FormBuilder,
        private clusterApi: ClusterApi, private snackbar: SnackbarService,
    ) {
    }

    submit() {
        this.clusterApi.listClusterLogsSnapshot({
            clusterUuid: this.data.cluster.uuid,
            maxLines: this.form.value.limit!,
        }).pipe(
            scan(((current, next) => {
                const newArray = current.slice();
                for (let line of next.lines) this.insertLogLine(newArray, line)
                return newArray.sort((l1, l2) => l1.timestamp.getTime() - l2.timestamp.getTime());
            }), [] as ClusterLogLine[]),
            last(),
        ).subscribe({
            next: (logs) => {
                const blob = new Blob([logs.map(x => x.line).join("\n")], { type: "text/plain" });
                const anchor = document.createElement("a");
                anchor.href = URL.createObjectURL(blob);
                const timeString = new Date().toISOString().split(".")[0].replace(":", "");
                anchor.download = `logs-${this.data.cluster.id}-${timeString}`;
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
                this.close("ok");
            },
            error: (err) => {
                console.error(err);
                this.isSubmitting$.next(false);
            },
        });
    }

    close(result?: DialogResult) {
        this.dialogRef.close(result);
    }

    insertLogLine(logs: ClusterLogLine[], newLine: ClusterLogLine) {
        let newLineTime = newLine.timestamp.getTime();
        for (let i = 0; i < logs.length; i++) {
            let logTime = logs[i].timestamp.getTime()
            if (
                (logTime > newLineTime) ||
                (logs[i].serverIndex === newLine.serverIndex && logTime === newLineTime && logs[i].lineNumber > newLine.lineNumber)
            ) {
                logs.splice(i, 0, newLine);
                return;
            }
        }
        logs.push(newLine);
        return;
    }
}
